/**
 * @module qbLinkage/detectChanges/detectMetricChanges
 * @description 仅 metric 类别：增删改重排、metricCount
 */

import type { IQBSnapshot, TQBEvent } from '../types';
import { fieldListSignature, snapshotList } from '../fieldSnapshot';
import {
  addedFields,
  diffRenames,
  findReorder,
  removedFields,
} from './fieldDiffUtils';
import type { IPartialQBChangeSet } from './partialChangeSet';

export function detectMetricChanges(
  prev: IQBSnapshot,
  next: IQBSnapshot,
): IPartialQBChangeSet {
  const events: TQBEvent[] = [];
  const prevMets = snapshotList(prev.columnMetrics, 'metric');
  const nextMets = snapshotList(next.columnMetrics, 'metric');

  const addedMetrics = addedFields(prevMets, nextMets);
  const removedMetrics = removedFields(prevMets, nextMets);

  if (addedMetrics.length) {
    events.push('metricAdded');
  }
  if (removedMetrics.length) {
    events.push('metricRemoved');
  }

  const reorderedMetrics = findReorder(prevMets, nextMets);
  if (reorderedMetrics) {
    events.push('metricReordered');
  }

  const metRenamed = diffRenames(prevMets, nextMets);
  const renamed = [...metRenamed];
  for (const r of metRenamed) {
    if (r.displayOnly) {
      events.push('fieldRenamed');
    } else {
      events.push('fieldIdentityRenamed');
    }
  }

  const metricCount = nextMets.length;
  if (metricCount !== prevMets.length) {
    events.push('metricCountChanged');
  }

  /**
   * 仅 aggregate（或签名中其它非结构字段）变更：id/列名/顺序未变。
   * columnSortSanitizeHandler 订阅本事件，通过 reconcile 合并 canonical 指标展示名。
   * 若同时有增删重排/改名，由 metricAdded 等事件触发 sanitize，无需重复派发。
   */
  const metricSigPrev = fieldListSignature(prev.columnMetrics, 'metric');
  const metricSigNext = fieldListSignature(next.columnMetrics, 'metric');
  if (metricSigPrev !== metricSigNext) {
    const structuralChange =
      addedMetrics.length > 0 ||
      removedMetrics.length > 0 ||
      !!reorderedMetrics ||
      metRenamed.length > 0;
    if (!structuralChange) {
      events.push('metricAggregateChanged');
    }
  }

  return {
    events,
    addedMetrics,
    removedMetrics,
    reorderedMetrics,
    renamed,
    metricCount,
  };
}
